import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

// Seus imports de estilos e componentes Styled Components
import timeFast from '../../../assets/production-flow/time-fast.svg';
import pcp from '../../../assets/production-flow/pcp.svg';
import filter from '../../../assets/stock/TableMovementHeader/filter.svg';
import { LeftSide, SectionName, SubHeaderContainer, TabsDiv, Tab, RightSide } from '../../../components/styles/subheader-style';
import { FilterDiv, FilterImg, FilterText, Content, Select, NewOPButton } from './style'; // Select e NewOPButton de './style'
import TablePCP from '../ProductionFashion/FashionPcp/index'
import BoardPCP from '../ProductionFashion/ProductionGrid/index'
import ModalContext from '../../../contexts/ModalContext';

// --- NOVOS IMPORTS DE COMPONENTES DE VISUALIZAÇÃO ---
// Lembre-se de ajustar os caminhos reais para TablePCP e BoardPCP
// Exemplo: import TablePCP from '../components/PCP/TablePCP';
// Exemplo: import BoardPCP from '../components/PCP/BoardPCP';
// Por enquanto, vou usar um placeholder para não causar erros de importação se eles ainda não existirem
/* const TablePCP = () => <p>Conteúdo da Tabela PCP (aqui será o TablePCP real)</p>; */
// --- FIM DOS NOVOS IMPORTS ---


// --- NOVAS INTERFACES E TIPOS ---
// Define os tipos de visualização para o PCP
type PCPViewType = 'table' | 'board';

// A interface do contexto que será passada via Outlet
// Note que agora ela inclui pcpView e setPCPView
export interface ProductionFashionOutletType {
    tabIndex: number;
    setTabIndex: React.Dispatch<React.SetStateAction<number>>;
    pcpView: PCPViewType; // Estado que indica qual visualização está ativa
    setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>; // Função para mudar a visualização
}
// --- FIM DAS NOVAS INTERFACES E TIPOS ---


// Definir os índices diretamente para clareza
const TAB_INDEX_ORDEM_PRODUCAO = 0;
const TAB_INDEX_PCP = 1;

const tabs = [
    { path: '', image: timeFast, name: 'Ordem de produção', index: TAB_INDEX_ORDEM_PRODUCAO },
    { path: 'pcp', image: pcp, name: 'PCP', index: TAB_INDEX_PCP }
];

// O ProductionFashionSubHeader agora recebe `pcpView` e `setPCPView` como props
const ProductionFashionSubHeader: React.FC<{
    tabIndexActived: number;
    pcpView: PCPViewType; // Recebe o estado da visualização do PCP
    setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>; // Recebe a função para atualizar a visualização do PCP
    onAddOPClick: () => void;
}> = ({ tabIndexActived, pcpView, setPCPView, onAddOPClick }) => { // Adicione pcpView e setPCPView às props desestruturadas
    const navigate = useNavigate();

    const isOrdemProducaoTab = tabIndexActived === TAB_INDEX_ORDEM_PRODUCAO;
    const isPCPTab = tabIndexActived === TAB_INDEX_PCP;
    const { setModalActive, setModalSection } = useContext(ModalContext)

    const addFashionOP = () => {
        setModalActive(true)
        setModalSection('addFashionOP')
    }

    const handleAddOPClick = useCallback(() => {
        // Usa `Maps('create')` para navegar para uma rota filha.
        // Se o path atual é /panel/production-fashion/pcp, ele irá para /panel/production-fashion/pcp/create
        navigate('pcp/create');
    }, [navigate]); // navigate é uma dependência, mas é estável

    return (
        <SubHeaderContainer>
            <LeftSide>
                <SectionName>
                    Produção
                </SectionName>
                <TabsDiv>
                    {tabs.map((tab, index) => (
                        <Tab
                            key={index}
                            $isFirst={index === 0}
                            $isLast={index === tabs.length - 1}
                            $isActive={index === tabIndexActived}
                            onClick={() => {
                                // A navegação permanece, garantindo que a URL mude corretamente
                                if (index !== tabIndexActived) navigate(tab.path);
                            }}
                        >
                            <img src={tab.image} alt={`${tab.path}-icon`} />
                            {tab.name}
                        </Tab>
                    ))}
                </TabsDiv>
            </LeftSide>
            <RightSide>
                {isOrdemProducaoTab && (
                    <FilterDiv>
                        <FilterImg src={filter} alt='filter-icon' />
                        <FilterText>Filtrar</FilterText>
                    </FilterDiv>
                )}

                {isPCPTab && ( // Renderiza os controles de visualização e botão "Adicionar OP" apenas se a aba PCP estiver ativa
                    <>
                        <span>Visualizar por</span>
                        <Select
                            value={pcpView} // O valor selecionado do <select> é controlado pelo estado `pcpView`
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPCPView(e.target.value as PCPViewType)} // Quando muda, atualiza o estado `pcpView` no componente pai
                        >
                            <option value="table">Tabela</option>
                            <option value="board">Quadro</option>
                        </Select>

                        <NewOPButton onClick={handleAddOPClick}>
                            + Adicionar OP
                        </NewOPButton>
                    </>
                )}
            </RightSide>
        </SubHeaderContainer>
    );
};

const ProductionFashionContent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState<number>(TAB_INDEX_ORDEM_PRODUCAO);
    // --- NOVO: Estado para controlar qual visualização do PCP está ativa ---
    const [pcpView, setPCPView] = useState<PCPViewType>('table'); // Inicia por padrão com a visualização de 'tabela'

    // *** NOVA FUNÇÃO PARA NAVEGAR PARA A CRIAÇÃO DE OP ***
    // Usa useCallback para que a função seja estável e não cause re-renderizações desnecessárias
    const handleAddOPClick = useCallback(() => {
        // Usa `Maps('create')` para navegar para uma rota filha.
        // Se o path atual é /panel/production-fashion/pcp, ele irá para /panel/production-fashion/pcp/create
        navigate('create');
    }, [navigate]); // navigate é uma dependência, mas é estável

    // Use useEffect para sincronizar o tabIndex com a URL
    useEffect(() => {
        const pathSegment = location.pathname.split('/').pop();
        const currentTabIndex = tabs.findIndex(tab => tab.path === pathSegment);

        if (currentTabIndex !== -1 && currentTabIndex !== tabIndex) {
            setTabIndex(currentTabIndex);
        } else if (pathSegment === 'production-fashion' && tabIndex !== TAB_INDEX_ORDEM_PRODUCAO) {
            setTabIndex(TAB_INDEX_ORDEM_PRODUCAO);
        }
    }, [location.pathname, tabIndex]);

    return (
        <>
            <ProductionFashionSubHeader
                tabIndexActived={tabIndex}
                pcpView={pcpView} // Passando o estado da visualização do PCP para o SubHeader
                setPCPView={setPCPView} // Passando a função para o SubHeader alterar o estado
                onAddOPClick={handleAddOPClick}
            />
            <Content>
                {/* --- Lógica de Renderização Condicional Principal --- */}
                {tabIndex === TAB_INDEX_PCP ? ( // SE A ABA ATIVA FOR A DO PCP...
                    pcpView === 'table' ? ( // ...ENTÃO, SE A VISUALIZAÇÃO SELECIONADA FOR 'TABELA'...
                        <TablePCP /> // Renderiza o componente TablePCP
                    ) : ( // ...SENÃO (se for 'quadro')...
                        <BoardPCP /> // Renderiza o componente BoardPCP
                    )
                ) : (
                    // SE A ABA ATIVA NÃO FOR A DO PCP (ex: "Ordem de Produção"),
                    // O Outlet renderiza o componente associado à rota filha padrão (ProductionFashionTable)
                    <Outlet
                        context={{ // O context é passado para o componente renderizado pelo Outlet
                            tabIndex,
                            setTabIndex,
                            // Estes podem ser passados para o Outlet caso componentes renderizados por ele (ex: ProductionFashionTable)
                            // precisem saber qual visualização está ativa ou mudá-la.
                            pcpView,
                            setPCPView
                        }}
                    />
                )}
            </Content>
        </>
    );
};

export default ProductionFashionContent;